import React from "react";
import { useGroups } from "../../hooks/custom/useGroups";
import { useRecipientsSelection } from "../../hooks/custom/useRecipientsSelection";
import GroupsHeader from "../ui/GroupsHeader";
import GroupsTable from "../tables/GroupsTable";
import CreateGroupModal from "../modals/CreateGroupModal";
import EditGroupModal from "../modals/EditGroupModal";
import DeleteGroupModal from "../modals/DeleteGroupModal";
import RecipientsSelectionModal from "../modals/RecipientsSelectionModal";

const Groups: React.FC = () => {
  const {
    // State
    groups,
    groupsLoading,
    groupsError,
    searchTerm,
    setSearchTerm,

    // Modal states
    showCreateGroupModal,
    setShowCreateGroupModal,
    showEditGroupModal,
    setShowEditGroupModal,
    showDeleteModal,
    setShowDeleteModal,
    editingGroup,
    deletingGroup,

    // Group form
    newGroup,
    setNewGroup,

    // Recipients modal
    selectedRecipients,
    setSelectedRecipients,
    showRecipientsModal,
    setShowRecipientsModal,
    availableRecipients,
    setAvailableRecipients,
    recipientsLoading,
    setRecipientsLoading,
    selectedRecipientIds,
    setSelectedRecipientIds,
    recipientsSearchTerm,
    setRecipientsSearchTerm,
    recipientsCurrentPage,
    setRecipientsCurrentPage,
    recipientsItemsPerPage,
    setRecipientsItemsPerPage,
    recipientsTotalCount,
    setRecipientsTotalCount,
    allRecipients,
    setAllRecipients,
    selectedAllFromDB,
    setSelectedAllFromDB,
    hasLoadedAllRecipients,
    setHasLoadedAllRecipients,
    loadingMoreRecipients,
    setLoadingMoreRecipients,

    // Computed values
    filteredAvailableRecipients,
    recipientsTotalPages,

    // Actions
    handleCreateGroup,
    handleEditGroup,
    handleUpdateGroup,
    handleDeleteGroup,
    confirmDeleteGroup,
    handleRemoveRecipientFromGroup,

    // State setters for modal management
    setEditingGroup,
    setDeletingGroup,
  } = useGroups();

  const {
    handleSelectRecipients,
    onRecipientSelection,
    handleRecipientsPageChange,
    handleRecipientsItemsPerPageChange,
    handleSelectAllFromDB,
    handleSelectAllVisible,
    handleClearAllSelections,
    handleLoadAllRecipients,
    handleAddSelectedRecipients,
  } = useRecipientsSelection(
    setRecipientsLoading,
    setAvailableRecipients,
    setRecipientsTotalCount,
    setAllRecipients,
    setSelectedAllFromDB,
    setHasLoadedAllRecipients,
    setLoadingMoreRecipients,
    allRecipients,
    recipientsCurrentPage,
    recipientsItemsPerPage,
    selectedRecipientIds,
    setSelectedRecipientIds,
    availableRecipients,
    setShowRecipientsModal,
    setRecipientsCurrentPage,
    setSelectedRecipients,
    setShowCreateGroupModal,
    setShowEditGroupModal,
    setRecipientsItemsPerPage,
    selectedRecipients
  );

  const handleCloseCreateModal = () => {
    setShowCreateGroupModal(false);
    setNewGroup({ name: "", description: "", recipients: [] });
    setSelectedRecipients([]);
  };

  const handleCloseEditModal = () => {
    setShowEditGroupModal(false);
    setEditingGroup(null);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingGroup(null);
  };

  const handleCloseRecipientsModal = () => {
    setShowRecipientsModal(false);
    setSelectedRecipientIds([]);
    setSelectedAllFromDB(false);
  };

  if (groupsError) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Error Loading Groups</h3>
          <p className="text-red-600 mt-1">{groupsError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <GroupsHeader
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onAddGroup={() => setShowCreateGroupModal(true)}
        groupsCount={groups.length}
      />

      {groupsLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading groups...</span>
        </div>
      ) : (
        <GroupsTable
          groups={groups}
          groupsLoading={groupsLoading}
          groupsError={groupsError}
          onEditGroup={handleEditGroup}
          onDeleteGroup={handleDeleteGroup}
        />
      )}

      {/* Create Group Modal */}
      <CreateGroupModal
        isOpen={showCreateGroupModal}
        onClose={handleCloseCreateModal}
        newGroup={newGroup}
        setNewGroup={setNewGroup}
        selectedRecipients={selectedRecipients}
        onRemoveRecipient={handleRemoveRecipientFromGroup}
        onSelectRecipients={handleSelectRecipients}
        onAddGroup={handleCreateGroup}
      />

      {/* Edit Group Modal */}
      <EditGroupModal
        isOpen={showEditGroupModal}
        onClose={handleCloseEditModal}
        editingGroup={editingGroup}
        newGroup={newGroup}
        setNewGroup={setNewGroup}
        selectedRecipients={selectedRecipients}
        onRemoveRecipient={handleRemoveRecipientFromGroup}
        onSelectRecipients={handleSelectRecipients}
        onUpdateGroup={handleUpdateGroup}
      />

      {/* Delete Confirmation Modal */}
      <DeleteGroupModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        deletingGroup={deletingGroup}
        onConfirmDelete={confirmDeleteGroup}
      />

      {/* Recipients Selection Modal */}
      <RecipientsSelectionModal
        isOpen={showRecipientsModal}
        onClose={handleCloseRecipientsModal}
        recipientsTotalCount={recipientsTotalCount}
        recipientsSearchTerm={recipientsSearchTerm}
        setRecipientsSearchTerm={setRecipientsSearchTerm}
        recipientsItemsPerPage={recipientsItemsPerPage}
        handleRecipientsItemsPerPageChange={handleRecipientsItemsPerPageChange}
        availableRecipients={availableRecipients}
        selectedRecipientIds={selectedRecipientIds}
        onRecipientSelection={onRecipientSelection}
        filteredAvailableRecipients={filteredAvailableRecipients}
        recipientsLoading={recipientsLoading}
        recipientsCurrentPage={recipientsCurrentPage}
        recipientsTotalPages={recipientsTotalPages}
        handleRecipientsPageChange={handleRecipientsPageChange}
        hasLoadedAllRecipients={hasLoadedAllRecipients}
        allRecipients={allRecipients}
        handleSelectAllFromDB={handleSelectAllFromDB}
        handleSelectAllVisible={handleSelectAllVisible}
        handleClearAllSelections={handleClearAllSelections}
        handleLoadAllRecipients={handleLoadAllRecipients}
        loadingMoreRecipients={loadingMoreRecipients}
        selectedAllFromDB={selectedAllFromDB}
        handleAddSelectedRecipients={handleAddSelectedRecipients}
      />
    </div>
  );
};

export default Groups;
